/* eslint-disable camelcase */
import React, {useState, useReducer} from 'react';
import Avatar from 'react-avatar';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPencilAlt} from '@fortawesome/free-solid-svg-icons';
import {TrashIcon} from '@heroicons/react/outline';

import Link from './Link';
import {useAppContext, useAnalytics} from '../hooks';
import {MemberBadge, CoachBadge} from './MemberBadge';
import {UpvoteButton} from './UpvoteButton';
import {CommentMarkdown} from './Markdown';
import {getEnglishTime, cx} from '../util';
import CommentEdit from './CommentEdit';
import {ReplyIcon, PinIcon} from './svg';
import {ReplyEditor} from './CommentEditor';
import KarmaPill from './KarmaPill';
import {PermissionType} from '../../../lib/constants/permissions';
import {postCommentVote, deleteCommentVote, updateComment} from '../api/api';

const minLength = 250;

const votingReducer = (voteState, voteAction) => {
  switch (voteAction.type) {
    case 'add':
      postCommentVote(voteAction.id);
      return {voted: true, count: voteState.count + 1};
    case 'remove':
      deleteCommentVote(voteAction.id);
      return {voted: false, count: voteState.count - 1};
    default:
      return voteState;
  }
};

const Comment = (props) => {
  const {
    questionID,
    replies,
    isReply,
    isGated,
    onTriggerSignup,
    onSubmit,
    submitted = false,
  } = props;

  const {
    id,
    voters,
    displayName,
    karma,
    uuid,
    coach_slug,
    data,
    profile_img,
    created_at,
    updated_at,
    pinned,
    is_member: isMember,
  } = props.data;

  const {tracker} = useAnalytics();
  const {currentUser, hasPermission} = useAppContext();
  const [readMore, setReadMore] = useState(data.text.length < minLength);
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [text, setText] = useState(data.text);
  const [vote, dispatchVote] = useReducer(votingReducer, {
    count: voters.length,
    voted: currentUser && voters.includes(currentUser.id),
  });

  const isTopAnswer = pinned && !isReply;
  const isAuthor = currentUser && currentUser.uuid === uuid;
  const isCoach = coach_slug;
  const isEdited = created_at !== updated_at;
  const timestamp = getEnglishTime(isEdited ? updated_at : created_at);

  // Permissions
  const canEdit = isAuthor || hasPermission(PermissionType.QUESTION_EDIT);
  const canPin = !isReply && hasPermission(PermissionType.QUESTION_PIN);
  const canArchive = isAuthor || hasPermission(PermissionType.QUESTION_ARCHIVE);
  const canReply = !isReply && !isGated && currentUser && currentUser.id;

  const onEdit = (questionData) => {
    setText(questionData);
    setIsEditing(false);
  };

  const onClickVote = () => {
    if (isGated) {
      onTriggerSignup();
    } else {
      tracker.questionUpvoteComment();
      dispatchVote({type: !vote.voted ? 'add' : 'remove', id});
    }
  };

  const onClickUser = (e) => {
    if (isGated) {
      e.preventDefault();
      onTriggerSignup();
    }
  };

  const onPin = async (isPinned) => {
    await updateComment(id, {pinned: isPinned});
    window.location.reload();
  };

  const onArchive = async () => {
    await updateComment(id, {archived: true});
    window.location.reload();
  };

  return (
    <div
      id={id}
      className={cx('comment', {
        'border border-gray-200 rounded-lg mb-3': !isReply,
        'comment--editing': isEditing,
      })}
    >
      <div className="pt-5 px-5">
        {isTopAnswer && <TopAnswerBadge />}
        <div className="flex items-center">
          <Link href={`/users/${uuid}`} onClick={onClickUser}>
            <Avatar
              className="bg-gray-100 size-10 mr-4 rounded-full shrink-0"
              name={displayName}
              src={profile_img || null}
              color="#f0f0f0"
              fgColor="#000000"
              size={40}
            />
          </Link>
          <div className="flex flex-col justify-center leading-tight gap-y-0.5">
            <div className="flex items-center">
              <Link href={`/users/${uuid}`} onClick={onClickUser} className="font-semibold text-sm">
                {displayName}
              </Link>
              {isCoach && (
                <CoachBadge
                  className="ml-1.5"
                  coachSlug={coach_slug}
                  onClick={() => tracker.questionClickCoachBadge({coach_slug})}
                />
              )}
              {!isCoach && isMember && (
                <MemberBadge
                  className="ml-1.5"
                  onClick={() => tracker.questionClickMemberBadge()}
                />
              )}
              {!isAuthor && (
                <KarmaPill
                  score={karma}
                  outline={isReply}
                  className="ml-1.5"
                  onClick={() => tracker.karmaClickScore({source: 'comment'})}
                />
              )}
            </div>
            <span className="block text-xs font-medium text-gray-400">
              <time
                dateTime={isEdited ? updated_at : created_at}
                title={isEdited ? updated_at : created_at}
              >
                {`${isEdited ? 'Updated ' : ''}${timestamp}`}
              </time>
            </span>
          </div>
        </div>
      </div>
      {isEditing ? (
        <CommentEdit
          id={id}
          currText={text}
          onFinish={onEdit}
          isReply={isReply}
        />
      ) : (
        <div className="prose relative ml-1 px-5">
          <div className={cx({'max-h-48 overflow-hidden': !readMore && !isReply})}>
            <CommentMarkdown>{text}</CommentMarkdown>
          </div>
          {!readMore && !isReply && (
            <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-b from-transparent to-white pointer-events-none" />
          )}
        </div>
      )}
      {!isEditing && (
        <div className="flex items-center px-5 mt-2 pb-5 flex-wrap-reverse">
          <div className="flex items-center flex-nowrap my-1">
            {!isReply && (
              <UpvoteButton
                voted={vote.voted}
                count={vote.count}
                onClick={onClickVote}
              />
            )}
            {canReply && (
              <button
                className="inline-block font-semibold text-gray-400 text-sm leading-7 px-3 bg-transparent rounded border border-transparent cursor-pointer transition-all whitespace-nowrap hover:bg-gray-50 hover:text-gray-700 active:outline-none focus:outline-none"
                onClick={() => setIsReplying(!isReplying)}
                type="button"
              >
                <span className="mr-2 -ml-1">
                  <ReplyIcon />
                </span>
                Comment
              </button>
            )}
            {canEdit && (
              <button
                className="inline-block font-semibold text-gray-400 text-sm leading-7 px-3 bg-transparent rounded border border-transparent cursor-pointer transition-all whitespace-nowrap hover:bg-gray-50 hover:text-gray-700 active:outline-none focus:outline-none"
                onClick={() => setIsEditing(true)}
                type="button"
              >
                <FontAwesomeIcon className="mr-2 -ml-1" icon={faPencilAlt} />
                Edit
              </button>
            )}
            {canPin && (
              <button
                className="inline-block font-semibold text-gray-400 text-sm leading-7 px-3 bg-transparent rounded border border-transparent cursor-pointer transition-all whitespace-nowrap hover:bg-gray-50 hover:text-gray-700 active:outline-none focus:outline-none"
                onClick={() => onPin(!pinned)}
                type="button"
              >
                <FontAwesomeIcon className="mr-2 -ml-1" icon={faPencilAlt} />
                {pinned ? 'Unpin' : 'Pin'}
              </button>
            )}
            {canArchive && (
              <button
                className="inline-block font-semibold text-gray-400 text-sm leading-7 px-3 bg-transparent rounded border border-transparent cursor-pointer transition-all whitespace-nowrap hover:bg-gray-50 hover:text-gray-700 active:outline-none focus:outline-none"
                onClick={onArchive}
                type="button"
              >
                <TrashIcon className="w-4 -ml-1 mr-1" />
                Delete
              </button>
            )}
          </div>
          {!readMore && !isReply && (
            <button
              type="button"
              className="inline-block font-semibold text-gray-400 text-sm leading-7 px-3 bg-transparent rounded border border-gray-200 cursor-pointer transition-all whitespace-nowrap hover:bg-gray-50 hover:text-gray-700 hover:border-transparent active:outline-none focus:outline-none ml-auto my-1"
              onClick={!isGated
                ? () => setReadMore(true)
                : onTriggerSignup}
            >
              Read more
            </button>
          )}
        </div>
      )}
      <CommentReplies
        onTriggerSignup={onTriggerSignup}
        replies={replies}
        isReplying={isReplying}
        onClickReply={() => setIsReplying(true)}
        questionID={questionID}
        parentCommentID={id}
        isGated={isGated}
        onSubmit={onSubmit}
        submitted={submitted}
      />
    </div>
  );
};

const TopAnswerBadge = () => (
  <div className="text-yellow-500 tracking-wider uppercase font-semibold text-sm leading-6 mb-4">
    <PinIcon />
    &nbsp;
    <span>top answer</span>
  </div>
);

const ReplyPreview = ({replies, onClick}) => {
  const {displayName, profile_img} = replies[0];

  const {text} = replies[0].data;

  const remaining = replies.length - 1;

  return (
    <button
      className="flex flex-col py-4 px-5 bg-gray-50 border-t border-gray-100 w-full hover:bg-gray-100 rounded-b-lg cursor-pointer"
      onClick={onClick}
      type="button"
    >
      <div className="flex items-center w-full">
        <Avatar
          className="bg-gray-100 size-10 mr-4 rounded-full shrink-0"
          name={displayName}
          src={profile_img || null}
          color="#f0f0f0"
          fgColor="#000000"
          size={24}
        />
        <span className="font-medium mr-3 whitespace-nowrap">
          {displayName}
        </span>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap [&_p]:m-0">
          {text}
        </span>
      </div>
      {replies.length > 1 && (
        <span className="pl-10 mt-1 inline-block [&_svg]:mr-2">
          <ReplyIcon />
          {`${remaining} more ${remaining > 1 ? 'replies' : 'reply'}`}
        </span>
      )}
    </button>
  );
};

function CommentReplies({
  onTriggerSignup,
  replies,
  isReplying,
  questionID,
  parentCommentID,
  onClickReply,
  isGated,
  onSubmit,
  submitted,
}) {
  if (!isReplying && (!replies || !replies.length)) {
    return null;
  }

  if (replies && replies.length && !isReplying) {
    return <ReplyPreview replies={replies} onClick={onClickReply} />;
  }

  return (
    <>
      <div className={cx('bg-gray-50 border-t border-gray-200', {'rounded-b-lg': isGated})}>
        {replies && replies.map((c) => (
          <Comment onTriggerSignup={onTriggerSignup} data={c} replies={null} isReply />
        ))}
      </div>
      {!isGated && (
        <ReplyEditor
          questionID={questionID}
          parentCommentID={parentCommentID}
          onSubmit={onSubmit}
          submitted={submitted}
        />
      )}
    </>
  );
}

export default Comment;
